import { Markup } from '@technology/web3d-plugin-markup';
import { Web3DViewerWrapper } from '@trimble/utilities';
import { Setter } from 'solid-js';
import { pruneViewerMarkupRefs } from '../base/shared-viewer-lifecycle';
import { hasCanonicalAnnotationOnViewer } from '../base/viewer-annotation-identity';
import { Measurement } from './measurement';

export interface MeasurementSeedContext {
  items(): Measurement[];
  setItems: Setter<Measurement[]>;
  applyFormatting(viewer: Web3DViewerWrapper): void;
  createOnViewer(
    measurement: Measurement,
    viewer: Web3DViewerWrapper,
    origin: symbol
  ): void;
  setHiddenOnViewer(measurement: Measurement, viewer: Web3DViewerWrapper): void;
}

export function pruneMeasurementsViewerRefs(
  removedViewerWrapper: Web3DViewerWrapper,
  setItems: Setter<Measurement[]>,
  getValidMarkups: (markups: Markup[]) => Markup[]
): void {
  pruneViewerMarkupRefs<Measurement, Markup>(
    removedViewerWrapper,
    setItems,
    (measurement) => getValidMarkups(measurement.markups),
    (measurement, markups) => ({ ...measurement, markups })
  );
}

/**
 * Projects canonical measurements onto a pane: creates missing annotations,
 * skips ones already stamped on the viewer. Safe to call repeatedly.
 */
export function projectMeasurementsOntoViewer(
  ctx: MeasurementSeedContext,
  viewerWrapper: Web3DViewerWrapper,
  replicationOrigin: symbol,
  options?: { applyFormatting?: boolean }
): void {
  const items = ctx.items();
  if (items.length === 0) {
    return;
  }

  // Viewer formatters must exist before seeded markups are created — setting
  // lengthUnit later re-formats all measurement labels synchronously.
  if (options?.applyFormatting !== false) {
    ctx.applyFormatting(viewerWrapper);
  }

  let projected = false;
  for (const measurement of items) {
    if (hasCanonicalAnnotationOnViewer(viewerWrapper.viewer, measurement.id)) {
      continue;
    }

    ctx.createOnViewer(measurement, viewerWrapper, replicationOrigin);

    if (measurement.hidden) {
      ctx.setHiddenOnViewer(measurement, viewerWrapper);
    }
    projected = true;
  }

  if (projected) {
    ctx.setItems([...ctx.items()]);
  }
}
